/**
 * Browser implementation of the Platform abstraction.
 *
 * Uses native Web APIs where available with graceful fallbacks. No Capacitor
 * imports — this file must remain Capacitor-free so the web bundle stays
 * lean.
 */

import type {
  GeoPosition,
  NetworkStatus,
  NotifyPayload,
  PhotoCapture,
  Platform,
  RealtimeConnection,
  RealtimeHandlers,
  SaveToDownloadsResult,
  SharePayload,
} from './types';

const VIBRATION_PATTERNS: Record<
  'tap' | 'success' | 'warning' | 'error',
  number | number[]
> = {
  tap: 10,
  success: [10, 40, 10],
  warning: [20, 60, 20],
  error: [40, 80, 40, 80, 40],
};

/** Browser download via a transient anchor element. */
function download_blob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    // Defer revocation so Safari has time to start the download.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }
  return Promise.resolve();
}

/** True if Web Share API exists and can share the given payload. */
function can_native_share(payload: SharePayload): boolean {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.share !== 'function'
  ) {
    return false;
  }
  if (payload.files && payload.files.length > 0) {
    if (typeof navigator.canShare !== 'function') {
      return false;
    }
    const files = payload.files.map(
      (f) => new File([f.blob], f.name, { type: f.mimeType }),
    );
    return navigator.canShare({ files });
  }
  return true;
}

async function share_payload(payload: SharePayload): Promise<boolean> {
  if (!can_native_share(payload)) {
    // Fallback 1: when a URL is present, copy it to the clipboard. Return
    // false to let the caller surface a toast.
    if (payload.url !== undefined) {
      try {
        await navigator.clipboard.writeText(payload.url);
        return false;
      } catch (err) {
        // Clipboard write may fail (permission, headless env). Surface to
        // logs via console so observability picks it up; still fall through
        // to the no-op false return below.
        console.warn('platform_web: clipboard fallback failed', err);
        return false;
      }
    }
    // Fallback 2: when files are present (e.g. a PDF) and no URL, download
    // the first file so the user still receives the document.
    if (payload.files && payload.files.length > 0) {
      const first = payload.files[0];
      await download_blob(first.blob, first.name);
    }
    return false;
  }

  const share_data: ShareData = {};
  if (payload.title !== undefined) share_data.title = payload.title;
  if (payload.text !== undefined) share_data.text = payload.text;
  if (payload.url !== undefined) share_data.url = payload.url;
  if (payload.files && payload.files.length > 0) {
    share_data.files = payload.files.map(
      (f) => new File([f.blob], f.name, { type: f.mimeType }),
    );
  }

  try {
    await navigator.share(share_data);
    return true;
  } catch (err) {
    // User cancellation surfaces as AbortError — not a real failure.
    if (err instanceof Error && err.name === 'AbortError') {
      return false;
    }
    throw err;
  }
}

async function clipboard_write(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

async function clipboard_read(): Promise<string> {
  return navigator.clipboard.readText();
}

async function notify_user(payload: NotifyPayload): Promise<boolean> {
  if (typeof Notification === 'undefined') {
    // Silent no-op in environments without the API (e.g., older browsers).
    return false;
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return false;
  }

  const notification = new Notification(payload.title, { body: payload.body });
  if (payload.href) {
    notification.onclick = () => {
      window.focus();
      window.location.href = payload.href ?? '/';
      notification.close();
    };
  }
  return true;
}

function vibrate_pattern(
  pattern: 'tap' | 'success' | 'warning' | 'error',
): void {
  if (typeof navigator.vibrate !== 'function') {
    return;
  }
  navigator.vibrate(VIBRATION_PATTERNS[pattern]);
}

/**
 * Web "secure" storage. NOT actually secure — localStorage is accessible
 * to any script on the same origin. Capacitor implementation uses the
 * native Keychain/Keystore. Callers should treat this as best-effort.
 */
const secure_storage = {
  set(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
    return Promise.resolve();
  },
  get(key: string): Promise<string | null> {
    return Promise.resolve(localStorage.getItem(key));
  },
  remove(key: string): Promise<void> {
    localStorage.removeItem(key);
    return Promise.resolve();
  },
};

const camera = {
  capturePhoto(): Promise<PhotoCapture> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.setAttribute('capture', 'environment');
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) {
          reject(new Error('platform_web: camera capture cancelled'));
          return;
        }
        resolve({ blob: file, mimeType: file.type || 'image/jpeg' });
      };
      input.click();
    });
  },
  isAvailable(): boolean {
    return typeof document !== 'undefined';
  },
};

const geolocation = {
  getCurrentPosition(): Promise<GeoPosition> {
    return new Promise((resolve, reject) => {
      if (typeof navigator === 'undefined') {
        reject(new Error('platform_web: geolocation unavailable'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            timestamp: pos.timestamp,
          });
        },
        (err) => {
          reject(new Error(err.message));
        },
      );
    });
  },
  isAvailable(): boolean {
    return typeof navigator !== 'undefined' && Boolean(navigator.geolocation);
  },
};

const realtime = {
  connect(url: string, handlers: RealtimeHandlers): RealtimeConnection {
    const ws = new WebSocket(url);
    ws.onmessage = (ev: MessageEvent) => {
      handlers.onMessage(ev.data as string);
    };
    if (handlers.onOpen)
      ws.onopen = () => {
        handlers.onOpen?.();
      };
    if (handlers.onClose)
      ws.onclose = () => {
        handlers.onClose?.();
      };
    if (handlers.onError)
      ws.onerror = (ev) => {
        handlers.onError?.(ev);
      };
    return {
      send: (data: string) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(data);
      },
      close: () => {
        ws.close();
      },
    };
  },
};

const network = {
  getStatus(): Promise<NetworkStatus> {
    const online = typeof navigator === 'undefined' ? true : navigator.onLine;
    return Promise.resolve(online ? 'online' : 'offline');
  },
  onStatusChange(listener: (status: NetworkStatus) => void): () => void {
    const on = () => {
      listener('online');
    };
    const off = () => {
      listener('offline');
    };
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  },
};

const biometrics = {
  isAvailable(): Promise<boolean> {
    return Promise.resolve(
      typeof window !== 'undefined' &&
        typeof window.PublicKeyCredential !== 'undefined',
    );
  },
  authenticate(_reason: string): Promise<boolean> {
    // Web stub: real WebAuthn assertion flow is out of scope for this package
    // promotion. Returns false so callers fall back to password re-auth.
    return Promise.resolve(false);
  },
};

export const webPlatform: Platform = {
  download: download_blob,
  share: share_payload,
  clipboard: {
    write: clipboard_write,
    read: clipboard_read,
  },
  async saveToDownloads(
    blob: Blob,
    filename: string,
  ): Promise<SaveToDownloadsResult> {
    await download_blob(blob, filename);
    return { uri: null };
  },
  // The browser owns downloaded files; there is nothing to open from here.
  openFile: () => Promise.resolve(false),
  // Web notifications handle their own click via `href` (see notify_user).
  onNotificationTap: () => () => undefined,
  notify: notify_user,
  vibrate: vibrate_pattern,
  secureStorage: secure_storage,
  isNative: () => false,
  camera,
  geolocation,
  realtime,
  network,
  biometrics,
};
